import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
// The same grouping the Invoices page does, restricted to the merchants we
// signed and added to Olive. This page is that page with a narrower feed.
import { buildBillingStateIndex, groupBillableByMonth, PAYMENT_STATE } from '../utils/billing';
import { canAccessMerchantPay, getPortalPathForUser, isMerchantSpecialistUser } from '../utils/portalRouting';
import BillingStatusIcon from '../components/BillingStatusIcon';
import MerchantOnboarding from '../components/MerchantOnboarding';
import MerchantInvite from '../components/MerchantInvite';
import '../styles/StaffPages.css';

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const SuperuserBilling = () => {
  const [transactions, setTransactions] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const user = await apiService.fetchCurrentUser();
        if (!canAccessMerchantPay(user)) {
          navigate(getPortalPathForUser(user));
          return;
        }
        setCurrentUser(user);
        const [txnRes, merchantRes, overviewRes] = await Promise.all([
          apiService.getAllTransactionsSuperuser(),
          apiService.getBillableMerchants(),
          // Collection state is a nice-to-have on this page; a failure here must
          // not stop the figures rendering.
          apiService.getBillingOverview().catch(() => null),
        ]);
        setTransactions(txnRes?.transactions || []);
        setMerchants(Array.isArray(merchantRes?.results) ? merchantRes.results : []);
        setOverview(overviewRes);
      } catch (err) {
        console.error('Failed to load billing data:', err);
        setError('Failed to load transactions.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  // Same shape the Invoices page builds, but only for the merchants we signed.
  const grouped = useMemo(
    () => groupBillableByMonth(transactions, merchants),
    [transactions, merchants]
  );

  const billingState = useMemo(() => buildBillingStateIndex(overview), [overview]);

  // Which merchant's setup row is open, and the profiles we have on file.
  const [openMerchant, setOpenMerchant] = useState(null);
  const [profiles, setProfiles] = useState({});
  useEffect(() => {
    const map = {};
    for (const p of overview?.profiles || []) map[p.merchant_key] = p;
    setProfiles(map);
  }, [overview]);

  /**
   * Every merchant we bill, by how far their ACH setup has got.
   *
   * Built from the merchant list rather than from transactions, which is the
   * whole point: a merchant with no sales produces no invoice row, and until
   * now that meant there was no way to reach them at all. Merchants we invited
   * before Olive registered them have no entry in that list either, so they
   * come from the profiles instead.
   */
  const setupGroups = useMemo(() => {
    const groups = { invited: [], none: [], pending: [], active: [] };
    for (const m of merchants) {
      const profile = profiles[m.id];
      if (!profile) groups.none.push({ merchant: m, profile: null });
      else if (profile.can_be_charged) groups.active.push({ merchant: m, profile });
      else groups.pending.push({ merchant: m, profile });
    }
    // Profiles with no merchant in the Olive snapshot: ones we invited before
    // Olive registered them. They need no reconciling — the profile key is the
    // normalized merchant name, so the day their offers sync they drop out of
    // this group and into the ones above carrying the same authorization.
    const inOlive = new Set(merchants.map((m) => String(m.id)));
    for (const [key, profile] of Object.entries(profiles)) {
      if (inOlive.has(String(key))) continue;
      groups.invited.push({
        merchant: { id: key, name: profile.display_name || key },
        profile,
      });
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => a.merchant.name.localeCompare(b.merchant.name));
    }
    return groups;
  }, [merchants, profiles]);

  const navSection = isMerchantSpecialistUser(currentUser) ? 'merchant-pay' : 'superuser';

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection={navSection} />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection={navSection} />
      <div className="admin-main">
        <div className="admin-content">
          <style>{`
            .billing-row-grid {
              display: grid;
              /* Trailing 20px column holds the collection-status icon. */
              grid-template-columns: 2fr 1fr 1fr 1fr 20px;
              gap: 12px;
              align-items: center;
            }
            @media (max-width: 640px) {
              .billing-row-grid {
                grid-template-columns: 1.5fr 1fr 20px !important;
              }
              .billing-col-hide-sm {
                display: none !important;
              }
              .billing-row-padding {
                padding: 12px 14px !important;
              }
              .billing-month-heading {
                font-size: 18px !important;
              }
            }
          `}</style>
          {currentUser?.is_superuser && (
            <BackButton text="Back to Dashboard" to="/superuser/dashboard/" />
          )}
          <h1 className="staff-page-header">Merchant Pay</h1>
          <p className="staff-page-subheader">Monthly commission invoiced by merchant</p>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              marginTop: '10px',
              fontSize: '12px',
              color: '#666',
            }}
          >
            {['pending', 'ready', 'processing', 'paid', 'failed'].map((key) => (
              <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <BillingStatusIcon state={PAYMENT_STATE[key]} size={15} />
                {PAYMENT_STATE[key].label.split(' — ')[0]}
              </span>
            ))}
          </div>

          {error && (
            <div style={{ marginTop: '24px', color: '#b00020' }}>{error}</div>
          )}

          {!error && grouped.length === 0 && (
            <div style={{ marginTop: '32px', color: '#666' }}>No transactions found.</div>
          )}

          {grouped.map((month) => (
            <div key={month.key} style={{ marginTop: '48px' }}>
              <h2 className="staff-section-header billing-month-heading" style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>
                {month.label}
              </h2>
              <p className="staff-page-content" style={{ marginBottom: '16px' }}>
                {month.merchants.length} merchant{month.merchants.length === 1 ? '' : 's'} with transactions
              </p>

              <div style={{
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'white',
              }}>
                <div
                  className="billing-row-grid billing-row-padding"
                  style={{
                    padding: '14px 20px',
                    background: '#f8f9fa',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#666',
                  }}
                >
                  <div>Merchant</div>
                  <div className="billing-col-hide-sm" style={{ textAlign: 'right' }}>Transactions</div>
                  <div className="billing-col-hide-sm" style={{ textAlign: 'right' }}>Sales</div>
                  <div style={{ textAlign: 'right' }}>Commission owed</div>
                  <div aria-hidden="true" />
                </div>
                {month.merchants.map((m) => (
                  <button
                    key={`${month.key}-${m.merchantId}`}
                    type="button"
                    onClick={() =>
                      navigate(`/superuser/invoices/${month.key}/${m.merchantId}/`)
                    }
                    className="billing-row-grid billing-row-padding"
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      border: 'none',
                      borderTop: '1px solid #f0f0f0',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      fontSize: '15px',
                      color: '#1a1a1a',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                  >
                    <div style={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.name}
                    </div>
                    <div className="billing-col-hide-sm" style={{ textAlign: 'right', color: '#666' }}>{m.txnCount}</div>
                    <div className="billing-col-hide-sm" style={{ textAlign: 'right', color: '#666' }}>{formatMoney(m.totalSales)}</div>
                    <div style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(m.totalCommission)}</div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <BillingStatusIcon state={billingState.lookup(m.merchantId, month.key)} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Every merchant we bill, whether or not they have sold anything and
              whether or not Olive has registered them yet. Onboarding used to be
              reachable only through a merchant-month invoice row, so a merchant
              with no sales could not be set up for ACH at all. */}
          <div style={{ marginTop: '40px' }}>
            <h2
              className="staff-section-header"
              style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}
            >
              Merchant setup
            </h2>
            <p className="staff-page-content" style={{ marginBottom: '16px' }}>
              {merchants.length} merchant{merchants.length === 1 ? '' : 's'} added to Olive
              {' · '}
              {setupGroups.active.length} authorized
              {' · '}
              {setupGroups.none.length} not set up
              {setupGroups.invited.length > 0 && (
                <>
                  {' · '}
                  {setupGroups.invited.length} added, not in Olive yet
                </>
              )}
            </p>

            {/* Reaching a merchant Olive has not registered. Every other route
                into this section starts from the snapshot, so without this a
                merchant we signed last week cannot be asked to authorize until
                their offers sync. */}
            <MerchantInvite
              onAdded={(profile) =>
                profile?.merchant_key &&
                setProfiles((prev) => ({ ...prev, [profile.merchant_key]: profile }))
              }
            />

            {(merchants.length > 0 || setupGroups.invited.length > 0) && (
              <div style={{
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'white',
              }}>
                {[
                  ['invited', 'Added — not in Olive yet', PAYMENT_STATE.pending],
                  ['none', 'Not set up for ACH', PAYMENT_STATE.none],
                  ['pending', 'Awaiting authorization', PAYMENT_STATE.pending],
                  ['active', 'Authorized', PAYMENT_STATE.ready],
                ].map(([key, label, state]) => (
                  setupGroups[key].length > 0 && (
                    <div key={key}>
                      <div style={{
                        padding: '12px 20px',
                        background: '#f8f9fa',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: '#666',
                        borderTop: '1px solid #f0f0f0',
                      }}>
                        {label} ({setupGroups[key].length})
                      </div>
                      {setupGroups[key].map(({ merchant, profile }) => {
                        const open = openMerchant === merchant.id;
                        return (
                          <div key={merchant.id} style={{ borderTop: '1px solid #f4f4f4' }}>
                            <button
                              type="button"
                              onClick={() => setOpenMerchant(open ? null : merchant.id)}
                              aria-expanded={open}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '14px 20px',
                                border: 'none',
                                background: open ? '#f8fafb' : 'white',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'inherit',
                                fontSize: '15px',
                                color: '#1a1a1a',
                              }}
                            >
                              <BillingStatusIcon state={state} size={16} />
                              <span style={{ flex: 1, fontWeight: 500 }}>{merchant.name}</span>
                              <span
                                style={{
                                  fontFamily: '"Baikal Book", sans-serif',
                                  fontSize: '13px',
                                  color: profile?.billing_email ? '#666' : '#9ca3af',
                                  fontStyle: profile?.billing_email ? 'normal' : 'italic',
                                }}
                              >
                                {profile?.billing_email || 'no billing email'}
                              </span>
                              {/* The app's pill, not a bare blue link: this is
                                  the same affordance every other staff page
                                  uses. A span rather than a button because the
                                  whole row is already the button — nesting one
                                  inside another is invalid. */}
                              <span
                                className="staff-page-button"
                                style={{ padding: '6px 14px', flex: '0 0 auto' }}
                              >
                                <span className="staff-page-button-text">
                                  {open ? 'Close' : profile ? 'Manage' : 'Set up'}
                                </span>
                              </span>
                            </button>
                            {open && (
                              <div style={{ padding: '0 20px 18px', background: '#fbfcfd' }}>
                                <MerchantOnboarding
                                  merchantKey={merchant.id}
                                  merchantName={merchant.name}
                                  profile={profile}
                                  onProfileSaved={(saved) =>
                                    saved &&
                                    setProfiles((prev) => ({ ...prev, [merchant.id]: saved }))
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserBilling;
